import { DatePipe, DecimalPipe, registerLocaleData } from '@angular/common';
import localeDe from '@angular/common/locales/de';
import localeDeCh from '@angular/common/locales/de-CH';
import localeEN from '@angular/common/locales/en';
import localeFr from '@angular/common/locales/fr';
import {
  DoBootstrap,
  Injector,
  isDevMode,
  LOCALE_ID,
  NgModule,
  Type,
} from '@angular/core';
import { createCustomElement } from '@angular/elements';
import { ReactiveFormsModule } from '@angular/forms';
import {
  BrowserModule,
  REMOVE_STYLES_ON_COMPONENT_DESTROY,
} from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ServiceWorkerModule } from '@angular/service-worker';
import {
  QUILL_CUSTOM_OPTIONS,
  QUILL_FORMATS,
  QUILL_MODULES,
} from '@configs/index';
import { environment } from '@env/environment';
import { VirtualScrollerModule } from '@iharbeck/ngx-virtual-scroller';
import {
  NgbAccordionModule,
  NgbDatepickerI18n,
  NgbPopoverModule,
  NgbTooltipModule,
} from '@ng-bootstrap/ng-bootstrap';
import { provideIcons } from '@ng-icons/core';
import { GOOGLE_MAPS_API_CONFIG } from '@ng-maps/google';
import { TranslateModule } from '@ngx-translate/core';
import { AppDatepickerI18n } from '@shared/components/boostrap-datepicker/app-datepicker-i18n';
import { DATA_DRIVEN_ICONS } from '@shared/constants/data-driven-icons.const';
import { StorageEnum } from '@shared/enums/storage.enum';
import { AttributeValuePipe } from '@shared/pipes/attribute-value.pipe';
import { EventRegisterBtnDisabledPipe } from '@shared/pipes/event-register-tooltip.pipe';
import {
  LinkWrapperPipe,
  SpanWrapperPipe,
} from '@shared/pipes/link-wrapper.pipe';
import { StringReplacementPipe } from '@shared/pipes/string-replacement.pipe';
import { YouTransformPipe } from '@shared/pipes/you-transform.pipe';
import { TINYMCE_SCRIPT_SRC } from '@tinymce/tinymce-angular';
import { NgxCaptchaModule } from 'ngx-captcha';
import { CookieService } from 'ngx-cookie-service';
import { EventBusService } from 'ngx-eventbus';
import { provideNgxMask } from 'ngx-mask';
import { QuillModule } from 'ngx-quill';
import { NgxVisibilityModule, NgxVisibilityService } from 'ngx-visibility';
import { CoreModule } from './core/core.module';
import { SharedModule } from './shared/shared.module';

// Import các component cần thiết cho custom elements
import { BootstrapComponent } from '@core/components/bootstrap/bootstrap.component';
import { NgMapsCoreModule } from '@ng-maps/core';
import { AuthenticationComponent } from '@shared/components/atoms/authentication/authentication.component';
import { RoundNumberPipe } from '@shared/pipes/round-number.pipe';
import { LoginComponent } from './components/auth/login/login.component';
import { CookiesAcceptanceComponent } from './components/cookies-acceptance/cookies-acceptance.component';
import { LanguagesComponent } from './components/master-layout/navs/components/languages/languages.component';
import { NavCustomLinkProfileComponent } from './components/master-layout/navs/components/nav-custom-link-profile/nav-custom-link-profile.component';
import { NavNewsMessagesComponent } from './components/master-layout/navs/components/nav-news-messages/nav-news-messages.component';
import { NavsComponent } from './components/master-layout/navs/navs.component';

const MAX_ELEMENT_LOAD_ATTEMPTS = 3;
const ELEMENT_LOAD_RETRY_DELAY_MS = 2000;

registerLocaleData(localeEN);
registerLocaleData(localeDe);
registerLocaleData(localeFr);
registerLocaleData(localeDeCh);

@NgModule({
  imports: [
    BrowserModule,
    TranslateModule.forRoot(),
    BrowserAnimationsModule,
    CoreModule,
    VirtualScrollerModule,
    NgxCaptchaModule,
    NgxVisibilityModule,
    SharedModule,
    ReactiveFormsModule,
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:30000',
    }),
    QuillModule.forRoot({
      modules: QUILL_MODULES,
      formats: QUILL_FORMATS,
      customOptions: QUILL_CUSTOM_OPTIONS,
    }),
    NgbAccordionModule,
    NgbPopoverModule,
    NgbTooltipModule,
    NgMapsCoreModule,
  ],
  declarations: [],
  bootstrap: [],
  providers: [
    provideIcons(DATA_DRIVEN_ICONS),
    DatePipe,
    SpanWrapperPipe,
    YouTransformPipe,
    EventRegisterBtnDisabledPipe,
    AttributeValuePipe,
    LinkWrapperPipe,
    StringReplacementPipe,
    RoundNumberPipe,
    DecimalPipe,
    NgxVisibilityService,
    EventBusService,
    CookieService,
    provideNgxMask(),
    { provide: NgbDatepickerI18n, useClass: AppDatepickerI18n },
    {
      provide: 'QUILL_CONFIG',
      useValue: {
        modules: QUILL_MODULES,
        formats: QUILL_FORMATS,
        customOptions: QUILL_CUSTOM_OPTIONS,
      },
    },
    { provide: LOCALE_ID, useFactory: getCurrentLocale, deps: [CookieService] },
    { provide: REMOVE_STYLES_ON_COMPONENT_DESTROY, useValue: false },
    {
      provide: GOOGLE_MAPS_API_CONFIG,
      useValue: {
        apiKey: environment.googleMapApiKey,
        libraries: ['places'],
      },
    },
    {
      provide: TINYMCE_SCRIPT_SRC,
      useValue:
        '/wp-content/themes/jip/assets/js/integration/tinymce/tinymce.min.js',
    },
  ],
})
export class AppModule implements DoBootstrap {
  constructor(private readonly injector: Injector) {}

  /** Custom elements backed by lazy chunks, defined by `registerComponents` on demand. */
  private readonly lazyElements: [string, () => Promise<Type<unknown>>][] = [
    [
      'jip-ventures',
      () =>
        import('./components/ventures/ventures.component').then(
          m => m.VenturesComponent
        ),
    ],
    [
      'jip-venture',
      () =>
        import(
          './components/ventures/components/venture/venture.component'
        ).then(m => m.VentureComponent),
    ],
    [
      'jip-organizations',
      () =>
        import('./components/organizations/organizations.component').then(
          m => m.OrganizationListComponent
        ),
    ],
    [
      'jip-organization',
      () =>
        import(
          './components/organizations/components/organization/organization.component'
        ).then(m => m.OrganizationComponent),
    ],
    [
      'jip-group',
      () =>
        import('./components/groups/components/group/group.component').then(
          m => m.GroupComponent
        ),
    ],
    [
      'jip-challenges',
      () =>
        import('./components/challenges/challenges.component').then(
          m => m.ChallengesComponent
        ),
    ],
    [
      'jip-challenge',
      () =>
        import(
          './components/challenges/components/challenge/challenge.component'
        ).then(m => m.ChallengeComponent),
    ],
    [
      'jip-peoples',
      () =>
        import('./components/peoples/peoples.component').then(
          m => m.PeopleListComponent
        ),
    ],
    [
      'jip-people',
      () =>
        import('./components/peoples/components/people/people.component').then(
          m => m.PeopleComponent
        ),
    ],
    [
      'jip-events',
      () =>
        import('./components/events/events.component').then(
          m => m.EventListComponent
        ),
    ],
    [
      'jip-event',
      () =>
        import('./components/events/components/event/event.component').then(
          m => m.EventComponent
        ),
    ],
    [
      'jip-news-messages',
      () =>
        import('./components/news-messages/news-messages.component').then(
          m => m.NewsMessagesComponent
        ),
    ],
    [
      'jip-reporting-chart',
      () =>
        import(
          '@shared/components/reporting-chart/reporting-chart.component'
        ).then(m => m.ReportingChartComponent),
    ],
    [
      'jip-search-results',
      () =>
        import(
          './components/search-results/containers/search-results/search-results.component'
        ).then(m => m.SearchResultsComponent),
    ],
    [
      'jip-event-calendar',
      () =>
        import(
          './components/dialogs/event-calendar-dialog/event-calendar-dialog.component'
        ).then(m => m.EventCalendarDialogComponent),
    ],
    [
      'jip-group-details',
      () =>
        import(
          './components/groups/components/group/group-details/group-details.component'
        ).then(m => m.GroupDetailsComponent),
    ],
    [
      'jip-profile',
      () =>
        import(
          './components/peoples/components/people/profile/profile.component'
        ).then(m => m.ProfileComponent),
    ],
    [
      'jip-process-token',
      () =>
        import('./components/auth/process-token/process-token.component').then(
          m => m.ProcessTokenComponent
        ),
    ],
    [
      'jip-footer',
      () =>
        import('./components/master-layout/footer/footer.component').then(
          m => m.FooterComponent
        ),
    ],
    [
      'jip-version-info',
      () =>
        import(
          './components/master-layout/footer/version-info/version-info.component'
        ).then(m => m.VersionInfoComponent),
    ],
    [
      'jip-power-by-bbv',
      () =>
        import(
          './components/master-layout/footer/power-by-bbv/power-by-bbv.component'
        ).then(m => m.PowerByBbvComponent),
    ],
    [
      'jip-change-password',
      () =>
        import(
          './components/auth/change-password/change-password.component'
        ).then(m => m.ChangePasswordComponent),
    ],
    [
      'jip-change-email',
      () =>
        import(
          './components/profile-actions/change-email/change-email.component'
        ).then(m => m.ChangeEmailComponent),
    ],
    [
      'jip-page-header',
      () =>
        import(
          './components/master-layout/page-header/page-header.component'
        ).then(m => m.PageHeaderComponent),
    ],
    [
      'jip-page-cover',
      () =>
        import(
          './components/master-layout/page-cover/page-cover.component'
        ).then(m => m.PageCoverComponent),
    ],
    [
      'jip-entity-list-by-token',
      () =>
        import(
          './components/entity-section/entity-list-by-token/entity-list-by-token.component'
        ).then(m => m.EntityListByTokenComponent),
    ],
    [
      'jip-admin-settings',
      () =>
        import('./components/admin-settings/admin-settings.component').then(
          m => m.AdminSettingsComponent
        ),
    ],
    [
      'jip-widget-metadata-table',
      () =>
        import(
          './components/entity-section/widget-metadata-table/widget-metadata-table.component'
        ).then(m => m.WidgetMetadataTableComponent),
    ],
    [
      'jip-challenge-tiles',
      () =>
        import(
          './components/list-tiles-widget/challenge-tiles/challenge-tiles.component'
        ).then(m => m.ChallengeTilesComponent),
    ],
    [
      'jip-venture-tiles',
      () =>
        import(
          './components/list-tiles-widget/venture-tiles/venture-tiles.component'
        ).then(m => m.VentureTilesComponent),
    ],
    [
      'jip-news-tiles',
      () =>
        import(
          './components/list-tiles-widget/news-tiles/news-tiles.component'
        ).then(m => m.NewsTilesComponent),
    ],
    [
      'jip-people-tiles',
      () =>
        import(
          './components/list-tiles-widget/people-tiles/people-tiles.component'
        ).then(m => m.PeopleTilesComponent),
    ],
    [
      'jip-organization-tiles',
      () =>
        import(
          './components/list-tiles-widget/organization-tiles/organization-tiles.component'
        ).then(m => m.OrganizationTilesComponent),
    ],
    [
      'jip-event-tiles',
      () =>
        import(
          './components/list-tiles-widget/event-tiles/event-tiles.component'
        ).then(m => m.EventTilesComponent),
    ],
    [
      'jip-event-registration',
      () =>
        import(
          './components/event-registration/containers/event-registration/event-registration.component'
        ).then(m => m.EventRegistrationComponent),
    ],
    [
      'jip-event-registration-form',
      () =>
        import(
          './components/event-registration/components/event-registration-form/event-registration-form.component'
        ).then(m => m.EventRegistrationFormComponent),
    ],
    [
      'jip-entity-creation-button',
      () =>
        import(
          '@shared/components/atoms/entity-creation-button/entity-creation-button.component'
        ).then(m => m.EntityCreationButtonComponent),
    ],
    [
      'jip-entity-counter',
      () =>
        import(
          '@shared/components/molecules/entity-counter/entity-counter.component'
        ).then(m => m.EntityCounterComponent),
    ],
    [
      'jip-simple-form-login',
      () =>
        import(
          '@shared/components/organisms/simple-form-login/simple-form-login.component'
        ).then(m => m.SimpleFormLoginComponent),
    ],
    [
      'jip-global-filter',
      () =>
        import('@shared/components/global-filter/global-filter.component').then(
          m => m.GlobalFilterComponent
        ),
    ],
    [
      'jip-thread-filter',
      () =>
        import(
          './components/master-layout/navs/components/thread-filter/thread-filter.component'
        ).then(m => m.ThreadFilterComponent),
    ],
    [
      'jip-rich-editor',
      () =>
        import(
          '@shared/components/rich-text-editor/rich-text-editor.component'
        ).then(m => m.RichTextEditorComponent),
    ],
    [
      'jip-community-selector',
      () =>
        import(
          './components/master-layout/list-layout-wrapper/components/community-selector/community-selector.component'
        ).then(m => m.CommunitySelectorComponent),
    ],
    [
      'jip-global-search',
      () =>
        import('@shared/components/global-search/global-search.component').then(
          m => m.GlobalSearchComponent
        ),
    ],
    [
      'jip-popup-blocker-notification',
      () =>
        import(
          '@shared/components/molecules/popup-blocker-detection-notification/popup-blocker-detection-notification.component'
        ).then(m => m.PopupBlockerDetectionNotificationComponent),
    ],
    [
      'jip-venture-tiles-cbcharta',
      () =>
        import(
          './components/list-tiles-widget/venture-tiles-cbcharta/venture-tiles-cbcharta.component'
        ).then(m => m.VentureTilesCbchartaComponent),
    ],
    [
      'jip-fancy-navigator',
      () =>
        import(
          './components/master-layout/navs/components/fancy-navigator/fancy-navigator.component'
        ).then(m => m.FancyNavigatorComponent),
    ],
    [
      'jip-post-details',
      () =>
        import(
          './components/news-messages/components/post-details/post-details.component'
        ).then(m => m.PostDetailsComponent),
    ],
    [
      'jip-form-editor',
      () =>
        import('@components/form-editor/form-editor.component').then(
          m => m.FormEditorComponent
        ),
    ],
    [
      'jip-group-tiles',
      () =>
        import(
          '@components/list-tiles-widget/group-tiles/group-tiles.component'
        ).then(m => m.GroupTilesComponent),
    ],
    [
      'jip-start-page',
      () =>
        import('@components/start-page/start-page.component').then(
          m => m.StartPageComponent
        ),
    ],
    [
      'jip-session-expiration-message',
      () =>
        import(
          '@shared/components/molecules/session-expiration-message/session-expiration-message.component'
        ).then(m => m.SessionExpirationMessageComponent),
    ],
    [
      'jip-featured-innovation-space',
      () =>
        import(
          '@shared/components/molecules/featured-innovation-space/featured-innovation-space.component'
        ).then(m => m.FeaturedInnovationSpaceComponent),
    ],
  ];

  /**
   * Loads and defines a lazy element only when its tag is on the page, so a page
   * downloads just the chunks of the widgets it contains. Tags already in the DOM are
   * handled right away; the others (Elementor popups, AJAX content) are picked up by a
   * MutationObserver, which only looks at the nodes WordPress just added. An element
   * whose chunk fails to load is retried a few times.
   */
  registerComponents(): void {
    const pending = new Map(this.lazyElements);
    const failedAttempts = new Map<string, number>();

    const defineElementsIn = (root: ParentNode): void => {
      if (pending.size === 0) return;

      const selector = [...pending.keys()].join(',');
      const tagsFound = new Set<string>();

      if (root instanceof Element && root.matches(selector)) {
        tagsFound.add(root.localName);
      }

      root
        .querySelectorAll(selector)
        .forEach(element => tagsFound.add(element.localName));

      tagsFound.forEach(loadElement);
    };

    const loadElement = (tag: string): void => {
      const load = pending.get(tag);

      pending.delete(tag);

      void this.defineElement(tag, load).then(isDefined => {
        if (isDefined) return;

        const attempts = (failedAttempts.get(tag) ?? 0) + 1;

        failedAttempts.set(tag, attempts);

        if (attempts >= MAX_ELEMENT_LOAD_ATTEMPTS) return;

        pending.set(tag, load);
        setTimeout(
          () => defineElementsIn(document),
          ELEMENT_LOAD_RETRY_DELAY_MS * attempts
        );
      });
    };

    defineElementsIn(document);

    new MutationObserver(mutations => {
      for (const { addedNodes } of mutations) {
        addedNodes.forEach(node => {
          if (node instanceof Element) defineElementsIn(node);
        });
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  public ngDoBootstrap(): void {
    this.registerComponents();
    customElements.define(
      'jip-navs',
      createCustomElement(NavsComponent, { injector: this.injector })
    );
    customElements.define(
      'jip-languages',
      createCustomElement(LanguagesComponent, { injector: this.injector })
    );
    customElements.define(
      'jip-nav-profile',
      createCustomElement(NavCustomLinkProfileComponent, {
        injector: this.injector,
      })
    );
    customElements.define(
      'jip-nav-news-messages',
      createCustomElement(NavNewsMessagesComponent, { injector: this.injector })
    );
    customElements.define(
      'jip-login',
      createCustomElement(LoginComponent, {
        injector: this.injector,
      })
    );
    customElements.define(
      'jip-cookies-acceptance',
      createCustomElement(CookiesAcceptanceComponent, {
        injector: this.injector,
      })
    );
    customElements.define(
      'jip-bootstrap',
      createCustomElement(BootstrapComponent, {
        injector: this.injector,
      })
    );
    customElements.define(
      'jip-authentication',
      createCustomElement(AuthenticationComponent, {
        injector: this.injector,
      })
    );
  }

  /** Loads the chunk of a lazy element and defines it; false when the chunk cannot be loaded. */
  private async defineElement(
    tag: string,
    load: () => Promise<Type<unknown>>
  ): Promise<boolean> {
    try {
      const component = await load();

      customElements.define(
        tag,
        createCustomElement(component, { injector: this.injector })
      );

      return true;
    } catch (error) {
      console.error(`Cannot load <${tag}>`, error);

      return false;
    }
  }
}

export function getCurrentLocale(cookieService: CookieService): string {
  const locale = cookieService.get(StorageEnum.locale);

  return locale || 'en';
}
